import { isEqual as deepIsEqual } from 'lodash';

import { Comparison, Condition, ConditionGroupOperator } from '@repo/sdk/models/components';

/**
 * Numeric value for the TRIM stage in compressor sequencer tables.
 * Defined locally to avoid coupling to the equipment-browser sequencer helpers.
 */
const TRIM_STAGE_VALUE = 3;

/** Type-safe value conversion utilities. */
const valueConverters = {
  toNumber: (value: any): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') return parseFloat(value);
    if (typeof value === 'boolean') return value ? 1 : 0;
    return NaN;
  },
  toString: (value: any): string => {
    if (value === null || value === undefined) return '';
    return String(value);
  },
  isNilOrEmpty: (value: any): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.length === 0;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
  },
} as const;

/** Validation utilities for checking whether a comparison can be performed. */
const validators = {
  isNumericComparison: (sourceValue: any, targetValue: any): boolean => {
    const sourceNum = valueConverters.toNumber(sourceValue);
    const targetNum = valueConverters.toNumber(targetValue);
    return !isNaN(sourceNum) && !isNaN(targetNum);
  },
  isStringComparison: (sourceValue: any, targetValue: any): boolean => {
    return (
      sourceValue !== null &&
      sourceValue !== undefined &&
      targetValue !== null &&
      targetValue !== undefined
    );
  },
} as const;

/** Equality comparison with optional numeric tolerance and deep object equality. */
const isEqual = (sourceValue: any, targetValue: any, tolerance?: number): boolean => {
  if (
    typeof sourceValue === 'number' &&
    typeof targetValue === 'number' &&
    tolerance !== undefined
  ) {
    return Math.abs(sourceValue - targetValue) <= tolerance;
  }
  if (typeof sourceValue === 'object' && typeof targetValue === 'object') {
    return deepIsEqual(sourceValue, targetValue);
  }
  return sourceValue === targetValue;
};

/** Checks if a target name exists in a sequencer object with non-zero stage values. */
const isInSequencerObj = (sourceValue: any, targetValue: any): boolean => {
  if (!sourceValue || !sourceValue.table || !Array.isArray(sourceValue.table)) {
    return false;
  }
  if (!targetValue || typeof targetValue !== 'string') {
    return false;
  }
  const entry = sourceValue.table.find((item: any) => item.name === targetValue);
  if (!entry || !Array.isArray(entry.stageValues)) {
    return false;
  }
  return entry.stageValues.some((value: number) => value !== 0);
};

/**
 * Checks if a compressor is in TRIM at the sequencer's current stage.
 * Looks up the compressor's row in the sequence table, then checks whether
 * its cell value at the current stage column equals TRIM (3).
 *
 * @param sequenceTable - Sequence table object (`{ table: Array<{ name, stageValues }> }`)
 * @param compressorAlias - The compressor's device alias
 * @param currentStage - The sequencer's current stage number (0-indexed)
 */
const isTrimAtCurrentStage = (
  sequenceTable: any,
  compressorAlias: any,
  currentStage?: any
): boolean => {
  if (!sequenceTable?.table || !Array.isArray(sequenceTable.table)) return false;
  if (!compressorAlias || typeof compressorAlias !== 'string') return false;
  if (currentStage == null) return false;
  const entry = sequenceTable.table.find((item: { name: string }) => item.name === compressorAlias);
  if (!entry?.stageValues || !Array.isArray(entry.stageValues)) return false;
  const stageIndex = Number(currentStage);
  if (isNaN(stageIndex) || stageIndex < 0 || stageIndex >= entry.stageValues.length) return false;
  return entry.stageValues[stageIndex] === TRIM_STAGE_VALUE;
};

/**
 * Map of comparison functions keyed by {@link Comparison} type.
 * `IsUnderEaiControl` is intentionally omitted — it requires Jotai `get()`
 * and is handled directly in the reactive condition evaluator.
 */
const comparisonHandlers: Partial<
  Record<Comparison, (source: any, target: any, extra?: any) => boolean>
> = {
  [Comparison.EqualTo]: (source: any, target: any, tolerance?: number) =>
    isEqual(source, target, tolerance),
  [Comparison.NotEqualTo]: (source: any, target: any, tolerance?: number) =>
    !isEqual(source, target, tolerance),
  [Comparison.LessThan]: (source: any, target: any) =>
    validators.isNumericComparison(source, target) &&
    valueConverters.toNumber(source) < valueConverters.toNumber(target),
  [Comparison.LessThanOrEqualTo]: (source: any, target: any) =>
    validators.isNumericComparison(source, target) &&
    valueConverters.toNumber(source) <= valueConverters.toNumber(target),
  [Comparison.GreaterThan]: (source: any, target: any) =>
    validators.isNumericComparison(source, target) &&
    valueConverters.toNumber(source) > valueConverters.toNumber(target),
  [Comparison.GreaterThanOrEqualTo]: (source: any, target: any) =>
    validators.isNumericComparison(source, target) &&
    valueConverters.toNumber(source) >= valueConverters.toNumber(target),
  [Comparison.Contains]: (source: any, target: any) =>
    validators.isStringComparison(source, target) &&
    valueConverters.toString(source).includes(valueConverters.toString(target)),
  [Comparison.StartsWith]: (source: any, target: any) =>
    validators.isStringComparison(source, target) &&
    valueConverters.toString(source).startsWith(valueConverters.toString(target)),
  [Comparison.EndsWith]: (source: any, target: any) =>
    validators.isStringComparison(source, target) &&
    valueConverters.toString(source).endsWith(valueConverters.toString(target)),
  [Comparison.IsNilOrEmpty]: (source: any, _target?: any) => valueConverters.isNilOrEmpty(source),
  [Comparison.IsNotNilOrEmpty]: (source: any, _target?: any) =>
    !valueConverters.isNilOrEmpty(source),
  [Comparison.IsInSequencerObj]: (source: any, target?: any) => isInSequencerObj(source, target),
  [Comparison.IsTrimAtCurrentStage]: (source: any, target: any, secondaryValue?: any) =>
    isTrimAtCurrentStage(source, target, secondaryValue),
  [Comparison.Some]: (source: any, target: any) => {
    if (!Array.isArray(target)) return false;
    const lowerSource = valueConverters.toString(source).toLowerCase();
    return target.some((item) => String(item).toLowerCase() === lowerSource);
  },
  [Comparison.Every]: (source: any, target: any) => {
    if (!Array.isArray(target)) return false;
    const lowerSource = valueConverters.toString(source).toLowerCase();
    return target.every((item) => String(item).toLowerCase() === lowerSource);
  },
};

/** Applies optional negation to a boolean result. */
export function applyNegation(result: boolean, negate?: boolean): boolean {
  return negate ? !result : result;
}

/** Logical operators for combining boolean condition results within a group. */
export const conditionGroupHandlers: Record<
  ConditionGroupOperator,
  (conditions: boolean[]) => boolean
> = {
  [ConditionGroupOperator.LogicalAnd]: (conditions: boolean[]) => {
    return conditions.every((condition) => condition);
  },
  [ConditionGroupOperator.LogicalOr]: (conditions: boolean[]) => {
    return conditions.some((condition) => condition);
  },
};

/**
 * Dispatches a comparison to the appropriate handler based on the condition's comparison type.
 * Handles special-case routing for tolerance, nil checks, and secondary values.
 */
export const performComparison = (
  condition: Condition,
  sourceValue: any,
  targetValue: any,
  secondaryValue?: any
): boolean => {
  const { comparison, tolerance } = condition;
  const handler = comparisonHandlers[comparison as Comparison];
  if (!handler) {
    console.warn(`Unknown comparison type: ${comparison}`);
    return false;
  }
  if (comparison === Comparison.IsTrimAtCurrentStage) {
    return handler(sourceValue, targetValue, secondaryValue);
  }
  if (comparison === Comparison.IsNilOrEmpty || comparison === Comparison.IsNotNilOrEmpty) {
    return handler(sourceValue, undefined);
  }
  if (
    (comparison === Comparison.EqualTo || comparison === Comparison.NotEqualTo) &&
    tolerance !== undefined
  ) {
    return handler(sourceValue, targetValue, tolerance);
  }
  return handler(sourceValue, targetValue);
};
